import {
  ATTACHMENT_LIMITS,
  type Attachment,
  type AttachmentList,
  type DocumentOrigin,
  type TurnOptions,
  type UploadAttachmentResponse,
} from "@esi/shared";
import { ApiError } from "../../api-errors.ts";
import { errorCopy } from "../../error-copy.ts";
import { t } from "../../i18n/i18n.ts";
import { putStorageFile } from "../../projects/storage-put.ts";
import { fileMediaType, fileProblem } from "../../projects/upload.ts";
import { holdReload } from "../../shell/build-refresh.ts";
import { attachmentApi } from "./api.ts";
import { advanceAttachmentSession } from "./session.ts";

export type LocalPhase =
  | "review"
  | "queued"
  | "uploading"
  | "processing"
  | "failed"
  | "ready"
  | "checking";
export interface LocalFile {
  key: string;
  title: string;
  sizeBytes: number;
  /** In memory only; discarded once confirmed. */
  file?: File | undefined;
  classification?: string | undefined;
  origin: DocumentOrigin;
  attachmentId?: string | undefined;
  phase: LocalPhase;
  progress: number;
  /** Confirmation won an explicit cancel; visible for review, never selected implicitly. */
  retained?: boolean;
  error?: string | undefined;
}
export interface DraftState {
  text: string;
  options?: TurnOptions | undefined;
  review: boolean;
  /**
   * Files picked or dropped since the review last opened: the composer opens the review for
   * them once, after a pause (attachments-polish D1). Zero when no review is due.
   */
  reviewDue: number;
  /** The question's text when the due batch was staged: typing since then holds the review. */
  reviewText?: string | undefined;
  files: readonly LocalFile[];
  selected: readonly string[];
  /** Holds send until both persisted history and sent metadata have been reconciled. */
  checkingSend: boolean;
  pendingText?: string | undefined;
  /** The held send's check failed before sending: nothing was sent (#207). */
  checkBeforeSend?: boolean | undefined;
}
type Io = ReturnType<typeof attachmentApi>;
type Put = typeof putStorageFile;
interface Attempt {
  target: UploadAttachmentResponse;
  stored: boolean;
  abort: AbortController;
  cancelling: boolean;
}

export function attachmentError(error: unknown): string {
  return error instanceof ApiError
    ? (errorCopy(error.code) ?? t("attachments.refused"))
    : t("attachments.failed");
}
export function attachmentActionError(error: unknown): string {
  return error instanceof ApiError
    ? (errorCopy(error.code) ?? t("attachments.actionFailed"))
    : t("attachments.actionFailed");
}
export const accessDenied = (error: unknown) =>
  error instanceof ApiError && (error.status === 403 || error.status === 404);

/** Supported metadata AND an explicit unsent entry are required; readiness cannot prove use. */
export function canSendAttachments(
  list: AttachmentList | undefined,
  ids: readonly string[],
): boolean {
  if (ids.length === 0) {
    return true;
  }
  return (
    ids.length <= ATTACHMENT_LIMITS.perMessage &&
    new Set(ids).size === ids.length &&
    list?.composer?.version === 1 &&
    // Selecting and sending follow the upload permission; managing alone (retry, delete) does not.
    list.composer.canUpload &&
    ids.every(
      (id) =>
        list.attachments.some((a) => a.id === id && a.status === "ready") &&
        list.composer?.attachmentStates.some((s) => s.attachmentId === id && s.sent === false),
    )
  );
}

/** Content refusals need different bytes. Unknown future failures never trigger blind retries. */
export const canRetryAttachment = (a: Attachment) =>
  a.status === "failed" &&
  ["ocr_failed", "index_drift", "embedding_failed", "processing_failed"].includes(
    a.failureReason ?? "",
  );

/** Only visible unsettled work polls. Terminal failures never spin indefinitely. */
export function attachmentPollMs(
  list: AttachmentList | undefined,
  visible: boolean,
): number | false {
  if (!visible) {
    return false;
  }
  if (list?.attachments.some((a) => a.status === "processing")) {
    return 2_000;
  }
  if (list?.attachments.some((a) => a.status === "failed" && a.failureReason === "spend_limit")) {
    return 15_000;
  }
  // An uploading record without a live browser attempt needs explicit removal/reselection.
  return false;
}

/** The private attempts hold targets; neither the public draft nor a query ever sees a URL. */
export class AttachmentDraft {
  private state: DraftState = {
    text: "",
    review: false,
    reviewDue: 0,
    files: [],
    selected: [],
    checkingSend: false,
  };
  private listeners = new Set<() => void>();
  private attempts = new Map<string, Attempt>();
  private running = new Set<string>();
  private io: Io;
  private release: (() => void) | undefined;
  private disposed = false;
  private invalidate: () => void = () => {};
  constructor(
    readonly conversationId: string,
    csrfToken: string,
    private put: Put = putStorageFile,
    io?: Io,
  ) {
    this.io = io ?? attachmentApi(conversationId, csrfToken);
  }
  configure(csrfToken: string, invalidate: () => void) {
    this.io = attachmentApi(this.conversationId, csrfToken);
    this.invalidate = invalidate;
  }
  snapshot = () => this.state;
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  private commit(patch: Partial<DraftState>) {
    if (this.disposed) {
      return;
    }
    this.state = { ...this.state, ...patch };
    const held =
      this.state.review ||
      this.state.files.length > 0 ||
      this.state.selected.length > 0 ||
      this.state.checkingSend;
    if (held && !this.release) {
      this.release = holdReload();
    } else if (!held && this.release) {
      this.release();
      this.release = undefined;
    }
    for (const listener of this.listeners) {
      listener();
    }
  }
  private update(key: string, patch: Partial<LocalFile>) {
    this.commit({ files: this.state.files.map((f) => (f.key === key ? { ...f, ...patch } : f)) });
  }
  edit(
    patch: Pick<
      Partial<DraftState>,
      "text" | "options" | "review" | "checkingSend" | "pendingText" | "checkBeforeSend"
    >,
  ) {
    this.commit(patch);
  }
  /** Stages files as To review. Never opens the review itself; an open review lists them. */
  add(files: readonly File[]) {
    this.commit({
      reviewDue: this.state.review ? 0 : this.state.reviewDue + files.length,
      ...(this.state.review || this.state.reviewDue > 0 ? {} : { reviewText: this.state.text }),
      files: [
        ...this.state.files,
        ...files.map(
          (file): LocalFile => ({
            key: crypto.randomUUID(),
            title: file.name,
            sizeBytes: file.size,
            file,
            phase: "review",
            origin: "internal",
            progress: 0,
          }),
        ),
      ],
    });
  }
  /** Opens the review (explicitly or once due); whatever was due is now in front of the user. */
  openReview() {
    this.commit({ review: true, reviewDue: 0 });
  }
  /** Closing never brings the review back by itself: the files stay To review. */
  closeReview() {
    this.commit({ review: false, reviewDue: 0 });
  }
  labels(key: string, classification: string, origin: DocumentOrigin) {
    if (this.state.files.some((f) => f.key === key && f.phase === "review")) {
      this.update(key, { classification, origin });
    }
  }
  /** A standalone file's origin changed on the server (it stays editable until first sent). */
  originChanged(attachmentId: string, origin: DocumentOrigin) {
    if (this.state.files.some((f) => f.attachmentId === attachmentId)) {
      this.commit({
        files: this.state.files.map((f) =>
          f.attachmentId === attachmentId ? { ...f, origin } : f,
        ),
      });
    }
  }
  select(id: string, selected: boolean) {
    this.commit({
      // Confirmed rows live in the authorized list. Deselect releases their local
      // hold, without deleting the server file or disturbing an active attempt.
      files: selected
        ? this.state.files
        : this.state.files.filter(
            (file) =>
              file.attachmentId !== id || file.retained || file.file || this.attempts.has(file.key),
          ),
      selected: selected
        ? [...new Set([...this.state.selected, id])]
        : this.state.selected.filter((s) => s !== id),
    });
  }
  remove(key: string) {
    const f = this.state.files.find((f) => f.key === key);
    if (f?.attachmentId) {
      this.select(f.attachmentId, false);
    }
    const files = this.state.files.filter((f) => f.key !== key);
    this.commit({
      files,
      // A due review only ever covers files still waiting for it.
      reviewDue: Math.min(this.state.reviewDue, files.filter((f) => f.phase === "review").length),
      ...(files.length === 0 && this.conversationId.startsWith("new:") ? { review: false } : {}),
    });
  }
  attach(list: AttachmentList) {
    const policy = list.composer?.uploadPolicy;
    if (!policy || !list.composer?.canUpload) {
      return;
    }
    // A standalone conversation uploads at its private levels only: anything else is the default.
    const allowed = list.composer.privateOnly ? policy.privateLevels : undefined;
    const files = this.state.files.map((f): LocalFile => {
      if (f.phase !== "review" || !f.file) {
        return f;
      }
      const error = fileProblem(f.file, policy.maxUploadMb);
      const chosen =
        f.classification && (!allowed || allowed.includes(f.classification))
          ? f.classification
          : undefined;
      return {
        ...f,
        classification: chosen ?? policy.defaultClassification,
        phase: error ? "failed" : "queued",
        ...(error ? { error } : {}),
      };
    });
    this.commit({ review: false, reviewDue: 0, files });
    this.pump();
  }
  private pump() {
    if (this.disposed) {
      return;
    }
    for (const file of this.state.files) {
      if (this.running.size >= 2) {
        break;
      }
      if (file.phase === "queued" && !this.running.has(file.key)) {
        this.running.add(file.key);
        void this.transfer(file).finally(() => {
          this.running.delete(file.key);
          this.invalidate();
          this.pump();
        });
      }
    }
  }
  private async transfer(file: LocalFile) {
    this.update(file.key, { phase: "uploading", error: undefined });
    let attempt = this.attempts.get(file.key);
    try {
      if (!attempt) {
        if (!file.file || !file.classification) {
          throw new Error("missing_file");
        }
        const target = await this.io.intent({
          title: file.title,
          mediaType: fileMediaType(file.file) ?? "",
          sizeBytes: file.sizeBytes,
          classification: file.classification,
          origin: file.origin,
        });
        attempt = { target, stored: false, abort: new AbortController(), cancelling: false };
        this.attempts.set(file.key, attempt);
        this.update(file.key, { attachmentId: target.attachment.id });
      }
      if (!this.state.files.some((f) => f.key === file.key) || this.disposed) {
        attempt.cancelling = true;
      }
      if (attempt.cancelling) {
        await this.settleCancel(file.key, attempt);
        return;
      }
      if (!attempt.stored) {
        if (!file.file || Date.parse(attempt.target.upload.expiresAt) <= Date.now()) {
          // Expired targets cannot resume. Reconcile before cleanup so a confirmed file wins.
          const current = await this.io.get(attempt.target.attachment.id);
          if (current.status !== "uploading") {
            this.confirmed(file.key, current, !attempt.cancelling);
            return;
          }
          try {
            await this.io.delete(current.id);
          } catch {
            // Cleanup is best effort; leave an explicit reselect row rather than losing it.
          }
          this.attempts.delete(file.key);
          this.update(file.key, {
            phase: "failed",
            attachmentId: undefined,
            error: t("attachments.reselect"),
          });
          return;
        }
        await this.put(attempt.target.upload, file.file, attempt.abort.signal, (sent, total) =>
          this.update(file.key, { progress: total > 0 ? Math.round((sent / total) * 100) : 0 }),
        );
        attempt.stored = true;
      }
      if (attempt.cancelling) {
        await this.settleCancel(file.key, attempt);
        return;
      }
      let confirmed: Attachment;
      try {
        // Confirm is deliberately not aborted: a cancellation waits for its outcome.
        confirmed = await this.io.confirm(attempt.target.attachment.id);
      } catch (error) {
        // Never mint another intent after a lost confirm response.
        const current = await this.io.get(attempt.target.attachment.id);
        if (current.status === "uploading") {
          if (attempt.cancelling) {
            await this.settleCancel(file.key, attempt);
            return;
          }
          if (error instanceof ApiError && error.code === "upload_missing") {
            attempt.stored = false;
            throw error;
          }
          confirmed = await this.io.confirm(current.id);
        } else {
          confirmed = current;
        }
      }
      this.confirmed(file.key, confirmed, !attempt.cancelling);
    } catch (error) {
      if (attempt?.cancelling) {
        try {
          await this.settleCancel(file.key, attempt);
        } catch {
          this.update(file.key, { phase: "checking", error: t("attachments.retryCheck") });
        }
      } else {
        this.update(file.key, { phase: "failed", error: attachmentError(error) });
      }
    }
  }
  private confirmed(key: string, attachment: Attachment, select = true) {
    this.attempts.delete(key);
    this.update(key, {
      phase:
        attachment.status === "ready"
          ? "ready"
          : attachment.status === "failed"
            ? "failed"
            : "processing",
      file: undefined,
      retained: !select,
      error: undefined,
    });
    // Local uploads are selected explicitly by Attach; recovered server rows are never auto-picked.
    this.select(attachment.id, select);
  }
  private async settleCancel(key: string, attempt: Attempt) {
    const id = attempt.target.attachment.id;
    try {
      const current = await this.io.get(id);
      if (current.status !== "uploading") {
        this.confirmed(key, current, !attempt.cancelling);
        return;
      }
      await this.io.delete(id);
    } catch (error) {
      if (!(error instanceof ApiError && error.status === 404)) {
        throw error;
      }
    }
    this.attempts.delete(key);
    this.remove(key);
  }
  async cancel(key: string) {
    const attempt = this.attempts.get(key);
    if (attempt) {
      attempt.cancelling = true;
      attempt.abort.abort();
      if (!this.running.has(key)) {
        try {
          await this.settleCancel(key, attempt);
        } catch {
          this.update(key, { phase: "checking", error: t("attachments.retryCheck") });
        }
        this.invalidate();
      }
    } else {
      // Intent may still be in flight: transfer sees its row gone and reconciles on return.
      this.remove(key);
    }
  }
  retry(key: string) {
    const attempt = this.attempts.get(key);
    const file = this.state.files.find((f) => f.key === key);
    if (attempt && file && !this.running.has(key)) {
      if (attempt.cancelling) {
        void this.cancel(key);
      } else {
        attempt.abort = new AbortController();
        this.update(key, { phase: "queued", error: undefined });
        this.pump();
      }
    }
  }
  hasAttempt(key: string) {
    return this.attempts.has(key);
  }
  reconcile(list: AttachmentList) {
    const sent = new Set(
      list.composer?.attachmentStates.filter((s) => s.sent).map((s) => s.attachmentId),
    );
    this.commit({
      selected: this.state.selected.filter((id) => !sent.has(id as never)),
      files: this.state.files.filter((f) => !f.attachmentId || !sent.has(f.attachmentId as never)),
    });
  }
  sent() {
    this.commit({
      text: "",
      files: [],
      selected: [],
      reviewDue: 0,
      checkingSend: false,
      pendingText: undefined,
      checkBeforeSend: undefined,
    });
  }
  dispose(notify = true) {
    this.disposed = true;
    for (const attempt of this.attempts.values()) {
      attempt.abort.abort();
    }
    this.attempts.clear();
    this.state = {
      text: "",
      review: false,
      reviewDue: 0,
      files: [],
      selected: [],
      checkingSend: false,
    };
    this.release?.();
    this.release = undefined;
    if (notify) {
      for (const listener of this.listeners) {
        listener();
      }
    }
  }
}

const drafts = new Map<string, AttachmentDraft>();
/** How long the staged files stay in view before their review opens by itself (D1). */
export const REVIEW_PAUSE_MS = 400;

export function attachmentDraft(conversationId: string, csrfToken: string) {
  let draft = drafts.get(conversationId);
  if (!draft) {
    draft = new AttachmentDraft(conversationId, csrfToken);
    drafts.set(conversationId, draft);
  }
  return draft;
}
/** Synchronously fence old work. During a parent render, disposal must not notify children.
 * Same-principal token renewal preserves only pure human text; actual attachment drafts clear
 * in full so removing file authority cannot silently turn their question into a text-only send.
 */
export function clearAttachmentDrafts(options: { preserveText?: boolean; notify?: boolean } = {}) {
  advanceAttachmentSession();
  for (const [id, draft] of [...drafts]) {
    const state = draft.snapshot();
    const keep =
      options.preserveText &&
      !state.review &&
      state.files.length === 0 &&
      state.selected.length === 0 &&
      !state.checkingSend &&
      !state.pendingText;
    draft.dispose(options.notify ?? true);
    drafts.delete(id);
    if (keep) {
      // No files/attempts survive here. The mounted hook configures live IO in its
      // effect before a later user Attach action can start a request.
      const replacement = new AttachmentDraft(id, "");
      replacement.edit({ text: state.text, options: state.options });
      drafts.set(id, replacement);
    }
  }
}
